import { Component, type ReactNode } from 'react';
import { Result, Button, Alert } from 'antd';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Simple Error Boundary — isolates page-level crashes.
 * Also resets on retry so user can stay in place without full reload.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown): void {
    // eslint-disable-next-line no-console
    console.error('UI ErrorBoundary caught', error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <Result
          status="error"
          title="Something went wrong"
          subTitle="The page encountered a client-side error. You can retry or reload."
          extra={[
            <Button key="retry" type="primary" onClick={this.reset}>
              Retry
            </Button>,
            <Button key="reload" onClick={() => window.location.reload()}>
              Reload
            </Button>,
          ]}
        >
          <Alert
            type="error"
            showIcon
            message={this.state.error.name}
            description={<pre style={{ fontSize: 11 }}>{this.state.error.message}</pre>}
          />
        </Result>
      );
    }
    return this.props.children;
  }
}
